import type { BrandModel, FieldSchemaModel } from '@/shared/generated'

import { useMemo } from 'react'
import { any, prop, uniqBy } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import {
	isFilterbaleField,
	prepareList,
	toAdaptedSchema,
	useColumnsSchemaQuery,
} from '@/entities/column'
import { useBrandsQuery, useCategoriesQuery } from '@/entities/core'
import { useOptimizationsQuery } from '@/entities/optimization'
import {
	useOptimizationGroupsQuery,
	useSalesEntitiesQuery,
} from '@/entities/optimization-group'
import {
	getAlertAnnotationDataChoices,
	usePricingAlertsAnnotationsQuery,
} from '@/entities/price-alert'
import { usePricingCampaignsQuery } from '@/entities/pricing-campaign'

import { getFieldConfig } from '@/widgets/quick-filters-bar'

import { getFiledConfig } from '@/shared/components/refinement-bar/helpers'
import { HiddenOnContext } from '@/shared/generated'
import { getTree } from '@/shared/lib/tree'

export function useAiDashboardFilterFieldsConfig() {
	const { t } = useTranslation()

	const schemaQuery = useColumnsSchemaQuery(HiddenOnContext.Assortment, {
		select: (data) => data?.filter(isFilterbaleField) || [],
	})
	const salesEntitiesQuery = useSalesEntitiesQuery({
		enabled: !!schemaQuery.data,
	})
	const categoriesQuery = useCategoriesQuery({})
	const { data: brand } = useBrandsQuery(
		{},
		{
			select: uniqBy<BrandModel>('id'),
		}
	)
	const optimizationGroupsQuery = useOptimizationGroupsQuery()
	const pricingCampaignsQuery = usePricingCampaignsQuery()
	const optimizationsQuery = useOptimizationsQuery({
		select: (data) =>
			data.map((optimization) => ({
				...optimization,
				id: optimization.optimization_group_id,
			})),
	})
	const alertsQuery = usePricingAlertsAnnotationsQuery({
		select: (data) => getAlertAnnotationDataChoices(data, t),
	})
	const posIds = useMemo(() => {
		const data = salesEntitiesQuery.data
		const posIdsField = schemaQuery.data?.find(
			(field) => field.id === 'pos_ids'
		)
		if (!posIdsField || !data) return []
		const list = prepareList(data.sales_entities, posIdsField)

		return getTree(list)
	}, [schemaQuery.data, salesEntitiesQuery.data])

	const choices = useMemo(
		() => ({
			sales_level: (salesEntitiesQuery.data?.sales_levels || []).map((e) => ({
				...e,
				id: e.level,
			})),
			sales_entity: salesEntitiesQuery.data?.sales_entities || [],
			category: categoriesQuery.data || [],
			brand: brand || [],
			optimization_group: optimizationGroupsQuery.data || [],
			pricing_campaign: pricingCampaignsQuery.data || [],
			optimization: optimizationsQuery.data || [],
			alert: alertsQuery.data || [],
			pos_ids: posIds || [],
		}),
		[
			salesEntitiesQuery,
			categoriesQuery,
			brand,
			optimizationGroupsQuery,
			pricingCampaignsQuery,
			optimizationsQuery,
			alertsQuery,
			posIds,
		]
	)
	const isLoading = any(prop('isLoading'), [
		salesEntitiesQuery,
		categoriesQuery,
		brand,
		optimizationGroupsQuery,
		pricingCampaignsQuery,
		optimizationsQuery,
		alertsQuery,
		posIds,
		schemaQuery,
	])

	return {
		fieldsConfig: {
			...getFiledConfig(
				schemaQuery.data?.map((schema: FieldSchemaModel) =>
					toAdaptedSchema(schema, t)
				) || [],
				choices
			),
			...getFieldConfig(t),
		},
		isLoading,
		choices,
		schema: schemaQuery.data,
	}
}
